import {
  Category,
  CategoryType,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CategoryFilters,
  CategoryListResponse,
  CategoryDetailResponse,
  ExportCategoryOptions
} from '@/types/category';
import { api } from '@/api/axios';

// Helper function to create mock export data
const createMockExport = async (options: ExportCategoryOptions): Promise<Blob> => {
  // Get actual categories data for export
  try {
    const response = await api.get('/categories?page=1&page_size=1000');
    const categories = Array.isArray(response.data.data) ? response.data.data : [];

    if (options.format === 'excel' || options.format === 'csv') {
      // Create CSV content
      const headers = ['Mã danh mục', 'Tên danh mục', 'Loại', 'Trạng thái', 'Ngày tạo'];

      if (options.includeDetails) {
        headers.push('Số lượng NVL', 'Danh mục cha', 'Bán thành phẩm');
      }

      if (options.includeNotes) {
        headers.push('Mô tả');
      }

      const rows = [headers.join(',')];

      categories.forEach((cat: any) => {
        // Filter by type if specified
        if (options.types && options.types.length > 0 && !options.types.includes(cat.type)) {
          return;
        }

        const row = [
          cat.code || '',
          `"${cat.name || ''}"`,
          cat.type || '',
          cat.status === 'active' ? 'Đang hoạt động' : 'Ngừng hoạt động',
          cat.createdAt ? new Date(cat.createdAt).toLocaleDateString('vi-VN') : '',
        ];

        if (options.includeDetails) {
          row.push(
            (cat.materialCount || 0).toString(),
            cat.parentName || '',
            cat.for_semi_material ? 'Có' : 'Không'
          );
        }

        if (options.includeNotes) {
          row.push(`"${cat.description || ''}"`);
        }

        rows.push(row.join(','));
      });

      const csvContent = rows.join('\n');
      return new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    } else {
      // Create PDF-like text content
      const content = `DANH SÁCH DANH MỤC
Ngày xuất: ${new Date().toLocaleDateString('vi-VN')}
Định dạng: ${options.format.toUpperCase()}
Khoảng thời gian: ${options.dateRange}
${options.types && options.types.length > 0 ? `Loại: ${options.types.join(', ')}` : 'Tất cả loại danh mục'}

${'='.repeat(80)}

${categories
  .filter((cat: any) => !options.types || options.types.length === 0 || options.types.includes(cat.type))
  .map((cat: any, index: number) => `
${index + 1}. ${cat.name}
   Mã: ${cat.code}
   Loại: ${cat.type}
   Trạng thái: ${cat.status === 'active' ? 'Đang hoạt động' : 'Ngừng hoạt động'}
   ${options.includeDetails ? `Số lượng NVL: ${cat.materialCount || 0}` : ''}
   ${options.includeDetails && cat.parentName ? `Danh mục cha: ${cat.parentName}` : ''}
   ${options.includeNotes && cat.description ? `Mô tả: ${cat.description}` : ''}
`).join('\n')}

${'='.repeat(80)}
Tổng số danh mục: ${categories.filter((cat: any) => !options.types || options.types.length === 0 || options.types.includes(cat.type)).length}
`;
      return new Blob([content], { type: 'text/plain;charset=utf-8;' });
    }
  } catch (error) {
    // Fallback to simple mock data if API fails
    const content = `Danh sách danh mục - ${new Date().toLocaleDateString('vi-VN')}
Không thể tải dữ liệu từ server.`;
    return new Blob([content], { type: 'text/plain;charset=utf-8;' });
  }
};

// Helper function to build query parameters
const buildQueryParams = (
  page: number = 1,
  pageSize: number = 10,
  filters?: CategoryFilters
): URLSearchParams => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (filters?.search) {
    params.append('search', filters.search);
  }

  if (filters?.status && filters.status.length > 0) {
    // Backend expects single status parameter, use first status for now
    params.append('status', filters.status[0]);
  }

  if (filters?.type && filters.type.length > 0) {
    // Backend supports comma-separated types for multiple type filtering
    params.append('type', filters.type.join(','));
  }

  if (filters?.parentId) {
    params.append('parent_id', filters.parentId);
  }

  // Add other supported filters from Swagger
  if (filters?.createdFrom) {
    params.append('created_from', filters.createdFrom);
  }

  if (filters?.createdTo) {
    params.append('created_to', filters.createdTo);
  }

  return params;
};

export const categoryApi = {
  // Get categories list with filters and pagination
  getCategories: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: CategoryFilters
  ): Promise<CategoryListResponse> => {
    try {
      const params = buildQueryParams(page, pageSize, filters);
      const response = await api.get(`/categories?${params}`);

      // Extract data from nested StandardResponse wrapper
      // Response structure: { data: { data: [...], total, page, pageSize }, timestamp }
      const responseData = response.data.data;
      const rawData = Array.isArray(responseData.data) ? responseData.data : [];

      // Map backend data to frontend Category type
      const mappedCategories = rawData.map((item: any) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        description: item.description || '',
        type: item.type as CategoryType,
        parentId: item.parentId,
        parentName: item.parentName,
        for_semi_material: item.for_semi_material,
        status: item.status,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        materialCount: item.materialCount,
        productCount: item.productCount,
        linkedRawMaterials: item.materialCount ?? 0,
        creator: item.createdBy || '',
        createdDate: item.createdAt || '',
        lastUsedDate: item.lastUsedDate,
      }));

      return {
        data: mappedCategories,
        total: responseData.total || 0,
        page: responseData.page || page,
        pageSize: responseData.pageSize || pageSize,
      };
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch categories'
      );
    }
  },

  // Get single category by code
  getCategory: async (code: string): Promise<Category> => {
    try {
      const response = await api.get(`/categories/${code}`);
      const item = response.data.data;

      // Map backend data to frontend Category type
      return {
        id: item.id,
        code: item.code,
        name: item.name,
        description: item.description || '',
        type: item.type as CategoryType,
        parentId: item.parentId,
        parentName: item.parentName,
        for_semi_material: item.for_semi_material,
        status: item.status,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        materialCount: item.materialCount,
        productCount: item.productCount,
        linkedRawMaterials: item.materialCount ?? 0,
        creator: item.createdBy || '',
        createdDate: item.createdAt || '',
        lastUsedDate: item.lastUsedDate,
      };
    } catch (error: any) {
      console.error(`Error fetching category ${code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Category with code ${code} not found`);
      }
      throw new Error(
        error.response?.data?.message || 'Failed to fetch category'
      );
    }
  },

  // Get category hierarchy
  getCategoryHierarchy: async (status?: 'active' | 'inactive'): Promise<Category[]> => {
    try {
      const params = new URLSearchParams();
      if (status) {
        params.append('status', status);
      }

      const response = await api.get(`/categories/hierarchy?${params}`);
      const rawData = Array.isArray(response.data.data) ? response.data.data : [];

      // Helper function to recursively map category with children
      const mapCategory = (item: any): any => ({
        id: item.id,
        code: item.code,
        name: item.name,
        description: item.description || '',
        type: item.type as CategoryType,
        parentId: item.parentId,
        parentName: item.parentName,
        for_semi_material: item.for_semi_material,
        status: item.status,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        materialCount: item.materialCount,
        productCount: item.productCount,
        linkedRawMaterials: item.materialCount ?? 0,
        creator: item.createdBy || '',
        createdDate: item.createdAt || '',
        lastUsedDate: item.lastUsedDate,
        children: item.children ? item.children.map(mapCategory) : [],
      });

      // Map backend data to frontend Category type
      const mappedCategories = rawData.map(mapCategory);

      return mappedCategories;
    } catch (error: any) {
      console.error('Error fetching category hierarchy:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch category hierarchy'
      );
    }
  },

  // Get category detail with raw materials
  getCategoryDetail: async (code: string): Promise<CategoryDetailResponse> => {
    try {
      const response = await api.get(`/categories/${code}`);
      const category = response.data.data;

      // For now, return empty raw materials since this endpoint might not include them
      // This can be enhanced when the backend provides category materials endpoint
      return {
        category,
        rawMaterials: [],
        totalMaterials: category.materialCount || 0,
      };
    } catch (error: any) {
      console.error(`Error fetching category detail ${code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Category with code ${code} not found`);
      }
      throw new Error(
        error.response?.data?.message || 'Failed to fetch category details'
      );
    }
  },

  // Create new category
  createCategory: async (data: CreateCategoryRequest): Promise<Category> => {
    try {
      const response = await api.post('/categories', data);
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating category:', error);
      if (error.response?.status === 422) {
        throw new Error(
          error.response?.data?.message || 'Validation error'
        );
      }
      throw new Error(
        error.response?.data?.message || 'Failed to create category'
      );
    }
  },

  // Update category
  updateCategory: async (data: UpdateCategoryRequest & { code: string }): Promise<Category> => {
    try {
      const { code, ...updateData } = data;
      const response = await api.put(`/categories/${code}`, updateData);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating category ${data.id}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Category with code ${data.id} not found`);
      }
      if (error.response?.status === 422) {
        throw new Error(
          error.response?.data?.message || 'Validation error'
        );
      }
      throw new Error(
        error.response?.data?.message || 'Failed to update category'
      );
    }
  },

  // Delete category
  deleteCategory: async (code: string): Promise<void> => {
    try {
      await api.delete(`/categories/${code}`);
    } catch (error: any) {
      console.error(`Error deleting category ${code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Category with code ${code} not found`);
      }
      if (error.response?.status === 409) {
        throw new Error('Cannot delete category with linked materials');
      }
      throw new Error(
        error.response?.data?.message || 'Failed to delete category'
      );
    }
  },

  // Get raw materials for a category
  getCategoryMaterials: async (
    categoryId: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      // This endpoint might not exist yet, so we'll use materials endpoint with category filter
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        category_id: categoryId,
      });

      const response = await api.get(`/materials?${params}`);
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        pageSize: response.data.pageSize || pageSize,
      };
    } catch (error: any) {
      console.error(`Error fetching materials for category ${categoryId}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch category materials'
      );
    }
  },

  // Export category data
  exportCategoryData: async (options: ExportCategoryOptions): Promise<Blob> => {
    try {
      // Build query parameters for export
      const params = new URLSearchParams();

      // Add format parameter
      params.append('format', options.format);

      // Handle type filters - backend expects individual 'type' parameters
      if (options.types && options.types.length > 0) {
        // If multiple types are selected, we need to handle this differently
        // For now, let's send all types and let backend filter
        options.types.forEach(type => params.append('type', type));
      }

      // Note: Backend doesn't currently support date range filtering for categories
      // but we can add these if needed in future
      // if (options.dateRange !== 'all') {
      //   params.append('date_range', options.dateRange);
      // }

      // Use the real API endpoint
      const response = await api.get(`/categories/export?${params}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Error exporting category data:', error);
      // Fallback to mock if API fails
      if (error.response?.status >= 500) {
        console.warn('API failed, falling back to mock export');
        return await createMockExport(options);
      }
      throw new Error(
        error.response?.data?.message || 'Failed to export category data'
      );
    }
  },

  // Get category statistics
  getCategoryStats: async () => {
    try {
      // Lightweight approach: fetch only totals using minimal page_size
      const [activeRes, inactiveRes] = await Promise.all([
        categoryApi.getCategories(1, 1, { status: ['active'] }),
        categoryApi.getCategories(1, 1, { status: ['inactive'] }),
      ]);

      const activeTotal = activeRes.total || 0;
      const inactiveTotal = inactiveRes.total || 0;

      return {
        totalCategories: activeTotal + inactiveTotal,
        activeCategories: activeTotal,
        inactiveCategories: inactiveTotal,
        // Keep the following fields for compatibility, but compute cheaply
        totalMaterials: 0,
        categoriesWithMaterials: 0,
      };
    } catch (error: any) {
      console.error('Error fetching category statistics:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch category statistics'
      );
    }
  },

  // Get unique creators for filter
  getCreators: async (): Promise<string[]> => {
    try {
      return api.get('/categories/creators').then(res => res.data);
    } catch (error: any) {
      console.error('Error fetching creators:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch creators'
      );
    }
  },

  // Import categories from file
  importCategories(file: File): Promise<{ success: boolean; message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/categories/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },

  // Get import template
  getImportTemplate(format: 'csv' | 'xlsx' = 'xlsx'): Promise<Blob> {
    return api.get(`/categories/import/template?format=${format}`, {
      responseType: 'blob',
    }).then(res => res.data);
  },
};
